// src/pages/Admin/Volunteer/Volunteer.tsx
import React, { useState } from "react";
import {
  Box,
  useTheme,
  Typography,
  CircularProgress,
  TextField,
  Button,
  Grid,
  Divider,
  Card,
  Avatar,
  Paper,
  Container,
  IconButton,
  Tooltip,
  Chip,
  alpha,
  Stack,
  CardContent,
  Alert,
  Fade,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import { toast } from "react-toastify";

// Icons
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import EmailIcon from "@mui/icons-material/Email";
import PhoneIcon from "@mui/icons-material/Phone";
import SchoolIcon from "@mui/icons-material/School";
import LanguageIcon from "@mui/icons-material/Language";
import RefreshIcon from "@mui/icons-material/Refresh";
import VolunteerActivismIcon from "@mui/icons-material/VolunteerActivism";
import PeopleAltIcon from "@mui/icons-material/PeopleAlt";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import DownloadIcon from "@mui/icons-material/Download";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import VisibilityIcon from "@mui/icons-material/Visibility";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import WorkIcon from "@mui/icons-material/Work";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";

// Components
import Filter from "../../../components/Admin/Common/Filter";
import Summary from "../../../components/Admin/Common/Summary";
import CustomTable from "../../../components/Admin/Common/Table";
import CustomModal, { CustomModalProps } from "../../../components/Admin/Common/CustomModal";
import Action from "../../../components/Admin/Common/Action";
import type { Volunteer } from "../../../types/volunteer";
import { useVolunteer } from "../../../components/Admin/Volunteer/hooks/useVolunteer";
import { volunteerService } from "../../../services/api/volunteerService";
import { useAuth } from "../../../contexts/AuthContext";

// Define a local interface that extends Volunteer with otherInterest
interface VolunteerWithOtherInterest extends Volunteer {
  otherInterest?: string;
}

const VolunteerManagement: React.FC = () => {
  const theme = useTheme();
  const { tokens } = useAuth();

  // Custom hook for volunteer data and actions
  const {
    volunteers,
    loading,
    error,
    success,
    selectedVolunteer,
    setSelectedVolunteer,
    handleViewVolunteer,
    handleDeleteVolunteer,
    handleUpdateVolunteer,
  } = useVolunteer();

  // Create a local refresh function since it's not available in the hook
  const refreshVolunteers = () => window.location.reload();

  // Local state for filtering & pagination
  const [selectedInterest, setSelectedInterest] = useState<string>("");
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Edit-related state with updated type
  const [isEditMode, setIsEditMode] = useState<boolean>(false);
  const [editData, setEditData] = useState<Partial<VolunteerWithOtherInterest>>({});
  const [formSubmitting, setFormSubmitting] = useState<boolean>(false);
  const [formError, setFormError] = useState<string | null>(null);

  // Ensure volunteers is always an array
  const safeVolunteers = Array.isArray(volunteers) ? volunteers : [];

  // Filter volunteers based on search term and interest
  const filteredVolunteers = safeVolunteers.filter((volunteer) => {
    const matchesSearch =
      !searchTerm ||
      volunteer.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      volunteer.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      false;

    const matchesInterest =
      !selectedInterest || 
      (volunteer.interests && 
       Array.isArray(volunteer.interests) && 
       volunteer.interests.includes(selectedInterest));

    return matchesSearch && matchesInterest;
  });

  // Calculate some statistics for the summary cards
  const volunteerStats = {
    total: safeVolunteers.length,
    fundraising: safeVolunteers.filter(v => v.interests && Array.isArray(v.interests) && v.interests.includes("Fundraising")).length,
    hospiceVisits: safeVolunteers.filter(v => v.interests && Array.isArray(v.interests) && v.interests.includes("Hospice Visits")).length,
    administrative: safeVolunteers.filter(v => v.interests && Array.isArray(v.interests) && v.interests.includes("Administrative")).length,
  };

  /**
   * Handles opening the edit modal with the volunteer's data
   * @param volunteer Volunteer object
   */
  const handleOpenEdit = (volunteer: Volunteer) => {
    // Convert to VolunteerWithOtherInterest type
    const volunteerWithOther: Partial<VolunteerWithOtherInterest> = {
      ...volunteer,
      otherInterest: ""
    };
    
    setEditData(volunteerWithOther); // Pre-fill the form with the existing data
    setIsEditMode(true);
  };

  /**
   * Handles closing the edit modal and resetting the form data
   */
  const handleCloseEdit = () => {
    setIsEditMode(false);
    setEditData({});
  };

  /**
   * Called when the user submits the edit form
   */
  const handleSubmitEdit = async () => {
    // Reset form state
    setFormError(null);
    setFormSubmitting(true);

    try {
      // Validate required fields
      if (!editData.name || !editData.email) {
        setFormError("Name and email are required fields");
        setFormSubmitting(false);
        return;
      }

      // Prepare interests array if not defined
      let finalInterests = editData.interests || [];
      
      // Handle 'Other' interest
      if (finalInterests.includes('Other') && editData.otherInterest) {
        finalInterests = finalInterests
          .filter(interest => interest !== 'Other')
          .concat(editData.otherInterest);
      }
      
      const volunteerData = {
        ...editData,
        interests: finalInterests,
      };

      if (editData.id) {
        // Update existing volunteer - exclude otherInterest field which is just for UI
        const { otherInterest, ...updateData } = volunteerData;
        await handleUpdateVolunteer(editData.id, updateData as Partial<Volunteer>);
      } else {
        // Create new volunteer - exclude otherInterest field which is just for UI
        const { otherInterest, ...createData } = volunteerData;
        await volunteerService.createVolunteer(createData as any);
        
        // Refresh volunteer list after creation
        refreshVolunteers();
        
        toast.success("Volunteer added successfully");
      }
      
      // Close the modal and reset form
      handleCloseEdit();
      
    } catch (error: any) {
      console.error("Error saving volunteer:", error);
      setFormError(error.message || "Failed to save volunteer");
    } finally {
      setFormSubmitting(false);
    }
  };

  // Export volunteers to CSV
  const handleExportData = () => {
    alert("Exporting volunteer data..."); // Replace with actual export logic
  };

  // Add new volunteer
  const handleAddVolunteer = () => {
    // Reset form data for a new volunteer
    setEditData({
      name: "",
      email: "",
      phone: "",
      interests: [],
      otherInterest: "",
      hoursAvailable: "",
      language: "",
      education: "",
      proficiency: undefined,
      referralSource: "",
      comments: ""
    });
    setIsEditMode(true);
  };

  // Update refresh button to use our refreshVolunteers function
  const handleRefreshData = () => {
    refreshVolunteers();
  };

  // If loading & no volunteers, show a spinner
  if (loading && safeVolunteers.length === 0) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="80vh"
        aria-label="Loading volunteers"
      >
        <CircularProgress size={60} thickness={4} />
      </Box>
    );
  }

  return (
    <Box
      component="section"
      sx={{
        backgroundColor: theme.palette.mode === 'light' 
          ? alpha(theme.palette.primary.light, 0.04)
          : theme.palette.background.default,
        minHeight: "100vh",
        pb: 6
      }}
      aria-label="Volunteer Management"
    >
      {/* Header Section */}
      <Paper 
        elevation={0} 
        sx={{ 
          borderRadius: 0, 
          py: 3, 
          px: 4, 
          mb: 3, 
          borderBottom: `1px solid ${alpha(theme.palette.divider, 0.5)}`
        }}
      >
        <Container maxWidth="xl">
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Box display="flex" alignItems="center">
              <PeopleAltIcon 
                sx={{ 
                  mr: 2, 
                  fontSize: 40, 
                  color: theme.palette.secondary.main 
                }} 
              />
              <div>
                <Typography component="h1" variant="h4" sx={{ fontWeight: 700 }}>
                  Volunteer Management
                </Typography>
                <Typography variant="subtitle1" color="text.secondary">
                  Manage volunteer information, skills, and assignments
                </Typography>
              </div>
            </Box>

            <Box display="flex" gap={1}>
              <Tooltip title="Add Volunteer" arrow>
                <Button
                  variant="contained"
                  color="secondary"
                  startIcon={<AddCircleOutlineIcon />}
                  onClick={handleAddVolunteer}
                  sx={{ 
                    borderRadius: 2,
                    mr: 2
                  }}
                >
                  Add Volunteer
                </Button>
              </Tooltip>
              <Tooltip title="Refresh Data" arrow>
                <IconButton 
                  onClick={handleRefreshData}
                  color="primary"
                  sx={{ 
                    backgroundColor: alpha(theme.palette.primary.main, 0.1),
                    '&:hover': {
                      backgroundColor: alpha(theme.palette.primary.main, 0.15),
                    }
                  }}
                >
                  <RefreshIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title="Export Data" arrow>
                <IconButton 
                  onClick={handleExportData}
                  sx={{ 
                    backgroundColor: alpha(theme.palette.success.main, 0.1),
                    color: theme.palette.success.main,
                    '&:hover': {
                      backgroundColor: alpha(theme.palette.success.main, 0.15),
                    }
                  }}
                >
                  <DownloadIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title="Help" arrow>
                <IconButton
                  sx={{ 
                    backgroundColor: alpha(theme.palette.info.main, 0.1),
                    color: theme.palette.info.main,
                    '&:hover': {
                      backgroundColor: alpha(theme.palette.info.main, 0.15),
                    }
                  }}
                >
                  <HelpOutlineIcon />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        </Container>
      </Paper>

      <Container maxWidth="xl">
        {/* Success and Error Messages */}
        {error && (
          <Alert 
            severity="error" 
            variant="outlined"
            sx={{ 
              mb: 3, 
              borderRadius: 2,
              borderColor: theme.palette.error.main
            }}
          >
            {error}
          </Alert>
        )}
        
        {success && (
          <Fade in={!!success}>
            <Alert 
              severity="success" 
              variant="filled"
              sx={{ 
                mb: 3, 
                borderRadius: 2,
                boxShadow: theme.shadows[2]
              }}
            >
              {success}
            </Alert>
          </Fade>
        )}

        {/* Summary Statistics */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} md={3}>
            <Card 
              elevation={2} 
              sx={{ 
                borderRadius: 3,
                height: '100%', 
                transition: 'all 0.2s ease-in-out',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: theme.shadows[8],
                }
              }}
            >
              <CardContent sx={{ p: 3 }}>
                <Box display="flex" alignItems="center" mb={2}>
                  <Avatar 
                    sx={{ 
                      width: 56, 
                      height: 56, 
                      bgcolor: alpha(theme.palette.primary.main, 0.1), 
                      color: theme.palette.primary.main,
                      mr: 2
                    }}
                  >
                    <PeopleAltIcon fontSize="large" />
                  </Avatar>
                  <Box>
                    <Typography color="text.secondary" variant="subtitle2">
                      Total Volunteers
                    </Typography>
                    <Typography variant="h4" fontWeight="bold">
                      {volunteerStats.total.toLocaleString()}
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} md={3}>
            <Card 
              elevation={2} 
              sx={{ 
                borderRadius: 3,
                height: '100%',
                transition: 'all 0.2s ease-in-out',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: theme.shadows[8],
                }
              }}
            >
              <CardContent sx={{ p: 3 }}>
                <Box display="flex" alignItems="center" mb={2}>
                  <Avatar 
                    sx={{ 
                      width: 56, 
                      height: 56, 
                      bgcolor: alpha(theme.palette.secondary.main, 0.1), 
                      color: theme.palette.secondary.main,
                      mr: 2
                    }}
                  >
                    <VolunteerActivismIcon fontSize="large" />
                  </Avatar>
                  <Box>
                    <Typography color="text.secondary" variant="subtitle2">
                      Fundraising
                    </Typography>
                    <Typography variant="h4" fontWeight="bold">
                      {volunteerStats.fundraising.toLocaleString()}
                    </Typography>
                  </Box>
                </Box>
                <Chip 
                  label={`${volunteerStats.total > 0 ? ((volunteerStats.fundraising / volunteerStats.total) * 100).toFixed(0) : 0}% of volunteers`} 
                  size="small" 
                  color="secondary" 
                  variant="outlined" 
                />
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} md={3}>
            <Card 
              elevation={2} 
              sx={{ 
                borderRadius: 3,
                height: '100%',
                transition: 'all 0.2s ease-in-out',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: theme.shadows[8],
                }
              }}
            >
              <CardContent sx={{ p: 3 }}>
                <Box display="flex" alignItems="center" mb={2}>
                  <Avatar 
                    sx={{ 
                      width: 56, 
                      height: 56, 
                      bgcolor: alpha(theme.palette.success.main, 0.1), 
                      color: theme.palette.success.main,
                      mr: 2
                    }}
                  >
                    <PersonOutlineIcon fontSize="large" />
                  </Avatar>
                  <Box>
                    <Typography color="text.secondary" variant="subtitle2">
                      Hospice Visits
                    </Typography>
                    <Typography variant="h4" fontWeight="bold">
                      {volunteerStats.hospiceVisits.toLocaleString()}
                    </Typography>
                  </Box>
                </Box>
                <Chip 
                  label={`${volunteerStats.total > 0 ? ((volunteerStats.hospiceVisits / volunteerStats.total) * 100).toFixed(0) : 0}% of volunteers`} 
                  size="small" 
                  color="success" 
                  variant="outlined" 
                />
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} md={3}>
            <Card 
              elevation={2} 
              sx={{ 
                borderRadius: 3,
                height: '100%',
                transition: 'all 0.2s ease-in-out',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: theme.shadows[8],
                }
              }}
            >
              <CardContent sx={{ p: 3 }}>
                <Box display="flex" alignItems="center" mb={2}>
                  <Avatar 
                    sx={{ 
                      width: 56, 
                      height: 56, 
                      bgcolor: alpha(theme.palette.info.main, 0.1), 
                      color: theme.palette.info.main,
                      mr: 2
                    }}
                  >
                    <WorkIcon fontSize="large" />
                  </Avatar>
                  <Box>
                    <Typography color="text.secondary" variant="subtitle2">
                      Administrative
                    </Typography>
                    <Typography variant="h4" fontWeight="bold">
                      {volunteerStats.administrative.toLocaleString()}
                    </Typography>
                  </Box>
                </Box>
                <Chip 
                  label={`${volunteerStats.total > 0 ? ((volunteerStats.administrative / volunteerStats.total) * 100).toFixed(0) : 0}% of volunteers`} 
                  size="small" 
                  color="info" 
                  variant="outlined" 
                />
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Search and Filters */}
        <Card 
          elevation={2} 
          sx={{ 
            mb: 4, 
            borderRadius: 3,
            overflow: 'visible' 
          }}
        >
          <CardContent sx={{ p: 3 }}>
            <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
              Search & Filter
            </Typography>
            <Filter
              searchValue={searchTerm}
              onSearchChange={setSearchTerm}
              searchPlaceholder="Search volunteers by name or email..."
              filterOptions={[
                { value: "Fundraising", label: "Fundraising" },
                { value: "Hospice Visits", label: "Hospice Visits" },
                { value: "Administrative", label: "Administrative" },
                { value: "Events", label: "Events" },
                { value: "Outreach", label: "Outreach" },
              ]}
              selectedFilter={selectedInterest}
              onFilterChange={setSelectedInterest}
              loading={loading}
            />
          </CardContent>
        </Card>

        {/* Table Section */}
        <Card elevation={3} sx={{ borderRadius: 3, overflow: 'hidden' }}>
          <CustomTable<Volunteer>
            columns={[
              { 
                label: "Volunteer", 
                key: "name",
                format: (value: string, row?: Volunteer) => (
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Avatar 
                      sx={{ 
                        bgcolor: alpha(theme.palette.secondary.main, 0.1),
                        color: theme.palette.secondary.main,
                        mr: 2,
                        width: 40, 
                        height: 40
                      }}
                    >
                      {value && value.charAt(0).toUpperCase()}
                    </Avatar>
                    <Box>
                      <Box sx={{ fontWeight: 600 }}>{value || 'Unknown'}</Box>
                      <Box sx={{ color: 'text.secondary', fontSize: '0.8rem' }}>{row?.email || ''}</Box>
                    </Box>
                  </Box>
                )
              },
              { 
                label: "Phone", 
                key: "phone",
                format: (value: string) => (
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <PhoneIcon sx={{ mr: 1, color: theme.palette.text.secondary, fontSize: 'small' }} />
                    <span>{value || "Not provided"}</span>
                  </Box>
                )
              },
              {
                label: "Interests",
                key: "interests",
                format: (val: string[]) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {val && Array.isArray(val) && val.length > 0 ? (
                      val.map((interest, i) => (
                        <Chip 
                          key={i} 
                          label={interest} 
                          size="small" 
                          variant="outlined"
                          color={
                            interest === "Fundraising" 
                              ? "secondary" 
                              : interest === "Hospice Visits" 
                                ? "success" 
                                : interest === "Administrative" 
                                  ? "info" 
                                  : "default"
                          }
                        />
                      ))
                    ) : (
                      <span>None specified</span>
                    )}
                  </Box>
                ),
              },
              { 
                label: "Hours Available", 
                key: "hoursAvailable",
                format: (value: string) => (
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <AccessTimeIcon sx={{ mr: 1, color: theme.palette.primary.main, fontSize: 'small' }} />
                    <Typography fontWeight={500}>{value || "Not specified"}</Typography>
                  </Box>
                )
              },
            ]}
            data={filteredVolunteers}
            loading={loading}
            page={page}
            rowsPerPage={rowsPerPage}
            onPageChange={setPage}
            onRowsPerPageChange={(newRows) => {
              setRowsPerPage(newRows);
              setPage(0);
            }}
            rowsPerPageOptions={[10, 25, 50, 100]}
            // Row Actions
            rowActions={(volunteer) => (
              <Action
                actions={[
                  {
                    label: "View Details",
                    icon: <VisibilityIcon fontSize="small" />,
                    onClick: () => handleViewVolunteer(volunteer.id),
                    color: theme.palette.info.main
                  },
                  {
                    label: "Edit Volunteer",
                    icon: <EditIcon fontSize="small" />,
                    onClick: () => handleOpenEdit(volunteer),
                    color: theme.palette.warning.main
                  },
                  {
                    label: "Delete Volunteer",
                    icon: <DeleteIcon fontSize="small" />,
                    onClick: () => handleDeleteVolunteer(volunteer.id),
                    color: theme.palette.error.main
                  },
                ]}
              />
            )}
          />
        </Card>
      </Container>

      {/* View Volunteer Modal */}
      {selectedVolunteer && (
        <CustomModal
          open={!!selectedVolunteer}
          title="Volunteer Details"
          onClose={() => setSelectedVolunteer(null)}
          
          fullWidth
        >
          {/* Use a Card container for a subtle elevation and padding */}
          <Card sx={{ p: 0, borderRadius: 3, overflow: 'hidden' }}>
            {/* Profile Header with Banner Background */}
            <Box 
              sx={{
                position: 'relative',
                height: 120,
                bgcolor: alpha(theme.palette.secondary.main, 0.8),
                mb: 5,
              }}
            >
              <Avatar
                sx={{
                  bgcolor: theme.palette.secondary.main,
                  width: 90,
                  height: 90,
                  position: 'absolute',
                  bottom: -40,
                  left: 30,
                  border: `4px solid ${theme.palette.background.paper}`,
                  boxShadow: theme.shadows[3],
                }}
              >
                <PersonOutlineIcon sx={{ fontSize: 40 }} />
              </Avatar>
            </Box>

            <Box px={3} pb={3}>
              <Box ml={14}>
                <Typography variant="h5" fontWeight="700" gutterBottom>
                  {selectedVolunteer.name || "No Name"}
                </Typography>
                <Stack direction="row" spacing={2} alignItems="center" mb={1}>
                  {selectedVolunteer.interests && selectedVolunteer.interests.map((interest, index) => (
                    <Chip 
                      key={index}
                      label={interest} 
                      color={
                        interest === "Fundraising" 
                          ? "secondary" 
                          : interest === "Hospice Visits" 
                            ? "success" 
                            : interest === "Administrative" 
                              ? "info" 
                              : "default"
                      }
                      variant="outlined"
                      size="small"
                    />
                  ))}
                </Stack>
                <Typography variant="caption" color="text.secondary">
                  Volunteer ID: {selectedVolunteer.id}
                </Typography>
              </Box>
            </Box>
          
            <Box p={3}>
              <Grid container spacing={4}>
                {/* Left Column */}
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle1" fontWeight="600" sx={{ mb: 2 }}>
                    Contact Details
                  </Typography>
                  
                  <Stack spacing={2}>
                    {/* Email */}
                    <Box display="flex" alignItems="center">
                      <Avatar 
                        sx={{ 
                          width: 36, 
                          height: 36, 
                          bgcolor: alpha(theme.palette.secondary.main, 0.1), 
                          color: theme.palette.secondary.main,
                          mr: 2
                        }}
                      >
                        <EmailIcon fontSize="small" />
                      </Avatar>
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          Email
                        </Typography>
                        <Typography variant="body2">
                          {selectedVolunteer.email || "No Email"}
                        </Typography>
                      </Box>
                    </Box>
                    
                    {/* Phone */}
                    <Box display="flex" alignItems="center">
                      <Avatar 
                        sx={{ 
                          width: 36, 
                          height: 36, 
                          bgcolor: alpha(theme.palette.secondary.main, 0.1), 
                          color: theme.palette.secondary.main,
                          mr: 2
                        }}
                      >
                        <PhoneIcon fontSize="small" />
                      </Avatar>
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          Phone
                        </Typography>
                        <Typography variant="body2">
                          {selectedVolunteer.phone || "No Phone"}
                        </Typography>
                      </Box>
                    </Box>
                  </Stack>
                  
                  <Divider sx={{ my: 3 }} />
                  
                  <Typography variant="subtitle1" fontWeight="600" sx={{ mb: 2 }}>
                    Education & Language
                  </Typography>
                  
                  <Stack spacing={2}>
                    {/* Education */}
                    <Box display="flex" alignItems="center">
                      <Avatar 
                        sx={{ 
                          width: 36, 
                          height: 36, 
                          bgcolor: alpha(theme.palette.info.main, 0.1), 
                          color: theme.palette.info.main,
                          mr: 2
                        }}
                      >
                        <SchoolIcon fontSize="small" />
                      </Avatar>
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          Education
                        </Typography>
                        <Typography variant="body2">
                          {selectedVolunteer.education || "Not specified"}
                        </Typography>
                      </Box>
                    </Box>
                    
                    {/* Language */}
                    <Box display="flex" alignItems="center">
                      <Avatar 
                        sx={{ 
                          width: 36, 
                          height: 36, 
                          bgcolor: alpha(theme.palette.info.main, 0.1), 
                          color: theme.palette.info.main,
                          mr: 2
                        }}
                      >
                        <LanguageIcon fontSize="small" />
                      </Avatar>
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          Language
                        </Typography>
                        <Typography variant="body2">
                          {selectedVolunteer.language || "Not specified"}
                        </Typography>
                      </Box>
                    </Box>
                  </Stack>
                </Grid>
                
                {/* Right Column */}
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle1" fontWeight="600" sx={{ mb: 2 }}>
                    Volunteering Details
                  </Typography>
                  
                  <Stack spacing={2}>
                    {/* Hours Available */}
                    <Box display="flex" alignItems="center">
                      <Avatar 
                        sx={{ 
                          width: 36, 
                          height: 36, 
                          bgcolor: alpha(theme.palette.success.main, 0.1), 
                          color: theme.palette.success.main,
                          mr: 2
                        }}
                      >
                        <AccessTimeIcon fontSize="small" />
                      </Avatar>
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          Hours Available
                        </Typography>
                        <Typography variant="body2">
                          {selectedVolunteer.hoursAvailable || "Not specified"}
                        </Typography>
                      </Box>
                    </Box>
                    
                    {/* Proficiency */}
                    <Box display="flex" alignItems="center">
                      <Avatar 
                        sx={{ 
                          width: 36, 
                          height: 36, 
                          bgcolor: alpha(theme.palette.success.main, 0.1), 
                          color: theme.palette.success.main,
                          mr: 2
                        }}
                      >
                        <WorkIcon fontSize="small" />
                      </Avatar>
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          Proficiency
                        </Typography>
                        <Typography variant="body2">
                          {selectedVolunteer.proficiency || "Not specified"}
                        </Typography>
                      </Box>
                    </Box>
                  </Stack>
                  
                  <Divider sx={{ my: 3 }} />
                  
                  <Typography variant="subtitle1" fontWeight="600" sx={{ mb: 2 }}>
                    Additional Information
                  </Typography>
                  
                  <Grid container spacing={2}>
                    {/* Referral Source */}
                    <Grid item xs={12}>
                      <Card variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                        <Typography variant="caption" color="text.secondary">
                          Referral Source
                        </Typography>
                        <Typography variant="body2" fontWeight={500}>
                          {selectedVolunteer.referralSource || "Not specified"}
                        </Typography>
                      </Card>
                    </Grid>
                    
                    {/* Comments */}
                    <Grid item xs={12}>
                      <Card variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                        <Typography variant="caption" color="text.secondary">
                          Comments
                        </Typography>
                        <Typography variant="body2">
                          {selectedVolunteer.comments || "No comments available"}
                        </Typography>
                      </Card>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
              
              <Box mt={4} display="flex" justifyContent="flex-end" gap={2}>
                <Button 
                  variant="outlined" 
                  color="primary" 
                  onClick={() => setSelectedVolunteer(null)}
                  sx={{ borderRadius: 2 }}
                >
                  Close
                </Button>
                <Button 
                  variant="contained" 
                  color="primary" 
                  startIcon={<EditIcon />}
                  onClick={() => {
                    setEditData(selectedVolunteer);
                    setIsEditMode(true);
                    setSelectedVolunteer(null);
                  }}
                  sx={{ borderRadius: 2 }}
                >
                  Edit Volunteer
                </Button>
              </Box>
            </Box>
          </Card>
        </CustomModal>
      )}

      {/* Edit Volunteer Modal */}
      <CustomModal
        open={isEditMode}
        title={editData.id ? "Edit Volunteer" : "Add New Volunteer"}
        onClose={handleCloseEdit}
        maxWidth="md"
        fullWidth
      >
        <Box
          component="form"
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 3,
            p: 2
          }}
        >
          {formError && (
            <Alert 
              severity="error" 
              variant="outlined"
              sx={{ 
                borderRadius: 2,
                borderColor: theme.palette.error.main
              }}
            >
              {formError}
            </Alert>
          )}
          
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography variant="h6" fontWeight={600} color="text.secondary" gutterBottom>
                Personal Information
              </Typography>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                label="Full Name"
                fullWidth
                value={editData.name || ""}
                onChange={(e) =>
                  setEditData((prev) => ({ ...prev, name: e.target.value }))
                }
                required
                variant="outlined"
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                label="Email"
                fullWidth
                type="email"
                value={editData.email || ""}
                onChange={(e) =>
                  setEditData((prev) => ({ ...prev, email: e.target.value }))
                }
                required
                variant="outlined"
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                label="Phone"
                fullWidth
                value={editData.phone || ""}
                onChange={(e) =>
                  setEditData((prev) => ({ ...prev, phone: e.target.value }))
                }
                variant="outlined"
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                label="Hours Available"
                fullWidth
                value={editData.hoursAvailable || ""}
                onChange={(e) =>
                  setEditData((prev) => ({
                    ...prev,
                    hoursAvailable: e.target.value,
                  }))
                }
                placeholder="e.g. Weekends, Evenings"
                variant="outlined"
              />
            </Grid>
            
            <Grid item xs={12}>
              <Divider />
            </Grid>
            
            <Grid item xs={12}>
              <Typography variant="h6" fontWeight={600} color="text.secondary" gutterBottom>
                Skills & Interests
              </Typography>
            </Grid>
            
            {/* Add Interest Checkboxes */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Areas of Interest
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }}>
                {['Administrative', 'Palliative/Hospice Home Visits', 'Inpatient Hospice Visits', 'Fundraising', 'Other'].map((interest) => (
                  <FormControlLabel
                    key={interest}
                    control={
                      <Checkbox
                        checked={editData.interests?.includes(interest) || false}
                        onChange={(e) => {
                          const { checked } = e.target;
                          setEditData((prev) => ({
                            ...prev,
                            interests: checked 
                              ? [...(prev.interests || []), interest]
                              : (prev.interests || []).filter(i => i !== interest)
                          }));
                        }}
                      />
                    }
                    label={interest}
                  />
                ))}
              </Box>
            </Grid>
            
            {/* Show Other Interest field if "Other" is selected */}
            {editData.interests?.includes('Other') && (
              <Grid item xs={12} md={6}>
                <TextField
                  label="Other Interest (please specify)"
                  fullWidth
                  value={editData.otherInterest || ""}
                  onChange={(e) =>
                    setEditData((prev) => ({ ...prev, otherInterest: e.target.value }))
                  }
                  required
                  variant="outlined"
                />
              </Grid>
            )}
            
            <Grid item xs={12} md={6}>
              <TextField
                select
                label="Proficiency"
                fullWidth
                value={editData.proficiency || ""}
                onChange={(e) => {
                  const value = e.target.value;
                  setEditData((prev) => ({ 
                    ...prev, 
                    proficiency: value === "" ? undefined : value as "Basic" | "Intermediate" | "Advanced"
                  }));
                }}
                variant="outlined"
                SelectProps={{
                  multiple: false,
                  native: true
                }}
              >
                <option value="">Select proficiency level</option>
                <option value="Basic">Basic</option>
                <option value="Intermediate">Intermediate</option>
                <option value="Advanced">Advanced</option>
              </TextField>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                label="Education"
                fullWidth
                value={editData.education || ""}
                onChange={(e) =>
                  setEditData((prev) => ({ ...prev, education: e.target.value }))
                }
                variant="outlined"
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                label="Language"
                fullWidth
                value={editData.language || ""}
                onChange={(e) =>
                  setEditData((prev) => ({ ...prev, language: e.target.value }))
                }
                variant="outlined"
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                select
                label="Referral Source"
                fullWidth
                value={editData.referralSource || ""}
                onChange={(e) =>
                  setEditData((prev) => ({ ...prev, referralSource: e.target.value }))
                }
                variant="outlined"
              >
                <option value="">Select referral source</option>
                <option value="Website">Website</option>
                <option value="Friend">Friend</option>
                <option value="Social Media">Social Media</option>
                <option value="Event">Event</option>
                <option value="Other">Other</option>
              </TextField>
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                label="Comments"
                fullWidth
                multiline
                rows={3}
                value={editData.comments || ""}
                onChange={(e) =>
                  setEditData((prev) => ({ ...prev, comments: e.target.value }))
                }
                variant="outlined"
              />
            </Grid>
          </Grid>

          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2, mt: 2 }}>
            <Button 
              variant="outlined" 
              onClick={handleCloseEdit}
              sx={{ borderRadius: 2 }}
              disabled={formSubmitting}
            >
              Cancel
            </Button>
            <Button 
              variant="contained" 
              color="primary"
              onClick={handleSubmitEdit}
              disabled={formSubmitting}
              sx={{ 
                borderRadius: 2,
                position: 'relative'
              }}
            >
              {formSubmitting ? (
                <>
                  <CircularProgress 
                    size={24} 
                    sx={{ 
                      position: 'absolute',
                      color: theme.palette.primary.contrastText
                    }}
                  />
                  <span style={{ opacity: 0 }}>
                    {editData.id ? "Update Volunteer" : "Add Volunteer"}
                  </span>
                </>
              ) : (
                editData.id ? "Update Volunteer" : "Add Volunteer"
              )}
            </Button>
          </Box>
        </Box>
      </CustomModal>
    </Box>
  );
};

export default VolunteerManagement;
